import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";

const STATUSES = [
    { value: "in_progress", label: "In progress" },
    { value: "review", label: "In review" },
    { value: "delivered", label: "Delivered" },
    { value: "approved", label: "Approved" },
];

export default function AddDeliverableDialog({ open, onOpenChange, clientId, onCreated }) {
    const [form, setForm] = useState({
        title: "", description: "", status: "in_progress",
        link: "", file_name: "", requires_approval: false, due: "",
    });
    const [loading, setLoading] = useState(false);

    const reset = () => setForm({
        title: "", description: "", status: "in_progress",
        link: "", file_name: "", requires_approval: false, due: "",
    });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post(`/clients/${clientId}/deliverables`, form);
            toast.success("Deliverable added", {
                description: form.requires_approval ? "Approval request sent to client." : undefined,
            });
            reset();
            onCreated?.(data);
        } catch (err) {
            toast.error("Could not add deliverable", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[540px] rounded-xl border-slate-200">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">Add a deliverable</DialogTitle>
                    <DialogDescription className="text-slate-500">Attach a file, link or just describe the work.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 pt-2">
                    <div className="space-y-2">
                        <Label className="text-slate-700">Title</Label>
                        <Input required value={form.title}
                               onChange={(e) => setForm({ ...form, title: e.target.value })}
                               data-testid="deliverable-title" className="ring-terra"
                               placeholder="Homepage design v3" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-slate-700">Description (optional)</Label>
                        <Textarea rows={3} value={form.description}
                                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                                  data-testid="deliverable-description" className="ring-terra"
                                  placeholder="A short note visible to your client…" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label className="text-slate-700">Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger data-testid="deliverable-status" className="ring-terra">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUSES.map((s) => (
                                        <SelectItem key={s.value} value={s.value} data-testid={`status-opt-${s.value}`}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-700">Due date (optional)</Label>
                            <Input type="date" value={form.due}
                                   onChange={(e) => setForm({ ...form, due: e.target.value })}
                                   data-testid="deliverable-due" className="ring-terra" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label className="text-slate-700">File name (optional)</Label>
                            <Input value={form.file_name}
                                   onChange={(e) => setForm({ ...form, file_name: e.target.value })}
                                   data-testid="deliverable-file" className="ring-terra"
                                   placeholder="design-v3.fig" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-700">Link (optional)</Label>
                            <Input type="url" value={form.link}
                                   onChange={(e) => setForm({ ...form, link: e.target.value })}
                                   data-testid="deliverable-link" className="ring-terra"
                                   placeholder="https://…" />
                        </div>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                        <div>
                            <p className="text-sm font-medium text-slate-900">Requires client approval</p>
                            <p className="text-xs text-slate-500">Adds a pending item to the client's Approvals tab.</p>
                        </div>
                        <Switch checked={form.requires_approval}
                                onCheckedChange={(v) => setForm({ ...form, requires_approval: v })}
                                data-testid="deliverable-requires-approval" />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={() => onOpenChange(false)}
                                data-testid="deliverable-cancel"
                                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading}
                                data-testid="deliverable-submit"
                                className="btn-terra rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-70">
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Add deliverable
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
